import { useEffect, useRef, useState } from 'react';
import { onSourceProgress } from '../../common/services/resultCacheApi';
import type { QueryColumn, ResultSourceProgressEvent } from '../../../../types/queryExecution';

export type PopulationProgressEntry = {
  uri: string;
  resultSetIndex: number;
  loadedRowCount: number;
  finalRowCount: number | undefined;
  state: ResultSourceProgressEvent['state'];
  columns: QueryColumn[];
  /**
   * Delivery mode for this result set. Defaults to `'windowed'` for backwards-compatible
   * callers that don't set it (e.g. terminal events from older code paths).
   */
  mode: 'inline' | 'windowed';
  /**
   * Inline rows accumulated from `rowsBatch` events (only populated when `mode === 'inline'`).
   * Switched back to a stable empty reference when mode flips to `'windowed'`.
   */
  rows: Record<string, unknown>[];
};

const RS_INDEX_RE = /\/rs\/(\d+)$/;
const EMPTY_ROWS: Record<string, unknown>[] = [];

/**
 * Max rows kept for the *transient* inline streaming preview. The pre-promotion
 * inline path streams up to FAST_PATH_MAX_ROWS (50K) rows as `rowsBatch`
 * progress events; accumulating + re-cloning the full array every batch is
 * O(n²) and re-renders a growing grid that promotion then discards — under
 * memory pressure this wedged the renderer JS thread (see
 * bigQueryResults.populate.spec.ts:162). The preview only needs to show that
 * data is arriving; the authoritative full result comes from either the
 * windowed source (after promotion) or the terminal `result.resultSets`
 * (small inline queries), so capping the preview loses no data.
 */
const INLINE_PREVIEW_CAP = 1000;

function parseResultSetIndex(uri: string): number | null {
  const m = RS_INDEX_RE.exec(uri);
  return m ? Number(m[1]) : null;
}

/** Handle for one deferred flush; `cancel` is safe to call after it fired. */
type FrameHandle = { cancel: () => void };

/**
 * Schedule `callback` for the next frame. rAF lands the update right before the
 * next paint (so a rows event arriving in the same frame still merges into a
 * single commit); hosts without rAF — jsdom without `pretendToBeVisual`, node —
 * fall back to a ~1-frame timer.
 */
function scheduleFrame(callback: () => void): FrameHandle {
  if (typeof requestAnimationFrame === 'function') {
    const id = requestAnimationFrame(() => callback());
    return { cancel: () => cancelAnimationFrame(id) };
  }
  const id = setTimeout(callback, 16);
  return { cancel: () => clearTimeout(id) };
}

export function usePopulationProgress(
  tabId: string | undefined,
  isExecuting: boolean,
  cacheGeneration?: number,
): Record<number, PopulationProgressEntry> | null {
  const [entries, setEntries] = useState<Record<number, PopulationProgressEntry> | null>(null);
  const columnsRef = useRef<Map<string, QueryColumn[]>>(new Map());
  const rowsRef = useRef<Map<string, Record<string, unknown>[]>>(new Map());
  /** URIs we've already seen an event for — drives the "first event" test below. */
  const seenRef = useRef<Set<string>>(new Set());
  /** Columns-only entries waiting for the frame boundary, keyed by result-set index. */
  const pendingRef = useRef<Map<number, PopulationProgressEntry>>(new Map());
  const frameRef = useRef<FrameHandle | null>(null);
  const cacheGenerationRef = useRef<number | undefined>(cacheGeneration);
  cacheGenerationRef.current = cacheGeneration;

  useEffect(() => {
    if (!isExecuting) {
      frameRef.current?.cancel();
      frameRef.current = null;
      pendingRef.current.clear();
      setEntries(null);
      columnsRef.current.clear();
      rowsRef.current.clear();
      seenRef.current.clear();
    }
  }, [isExecuting]);

  useEffect(() => {
    if (!tabId || !isExecuting) return;

    const uriPrefix = `result-cache://${tabId}/`;

    const cancelFrame = () => {
      frameRef.current?.cancel();
      frameRef.current = null;
    };

    /**
     * Apply any deferred columns-only entries — plus `entry` when one is given —
     * as ONE state update, so headers and their first rows paint in a single
     * commit. `entry` is applied last: for the same result set the newer event
     * supersedes the deferred columns-only snapshot.
     */
    const applyEntries = (entry?: PopulationProgressEntry) => {
      // Snapshot and clear BEFORE `setEntries` so the updater stays pure (React
      // may invoke it more than once, e.g. under StrictMode).
      const deferred = pendingRef.current.size > 0
        ? Array.from(pendingRef.current.values())
        : null;
      pendingRef.current.clear();
      if (!deferred && !entry) return;

      setEntries((prev) => {
        const next: Record<number, PopulationProgressEntry> = { ...prev };
        if (deferred) {
          for (const deferredEntry of deferred) next[deferredEntry.resultSetIndex] = deferredEntry;
        }
        if (entry) next[entry.resultSetIndex] = entry;
        return next;
      });
    };

    const unsubscribe = onSourceProgress((event: ResultSourceProgressEvent) => {
      if (!event.uri.startsWith(uriPrefix)) return;

      if (cacheGenerationRef.current !== undefined && event.generation < cacheGenerationRef.current) {
        return;
      }

      const rsIndex = parseResultSetIndex(event.uri);
      if (rsIndex === null) return;

      const isFirstEventForUri = !seenRef.current.has(event.uri);
      seenRef.current.add(event.uri);

      if (event.columns && !columnsRef.current.has(event.uri)) {
        columnsRef.current.set(event.uri, event.columns);
      }
      const columns = columnsRef.current.get(event.uri) ?? [];

      const mode: 'inline' | 'windowed' = event.mode ?? 'windowed';

      // Accumulate inline rows (capped preview); clear when promotion flips us
      // to windowed.
      let rows: Record<string, unknown>[];
      if (mode === 'windowed') {
        rowsRef.current.delete(event.uri);
        rows = EMPTY_ROWS;
      } else {
        let existing = rowsRef.current.get(event.uri);
        if (!existing) {
          existing = [];
          rowsRef.current.set(event.uri, existing);
        }
        if (
          event.rowsBatch &&
          event.rowsBatch.length > 0 &&
          existing.length < INLINE_PREVIEW_CAP
        ) {
          // Grow only up to the cap, then freeze. Slicing happens at most a
          // couple of times (cap reached within the first batches), so the
          // total clone cost is O(cap) instead of O(rows²/batch).
          for (
            let i = 0;
            i < event.rowsBatch.length && existing.length < INLINE_PREVIEW_CAP;
            i++
          ) {
            existing.push(event.rowsBatch[i]);
          }
          rows = existing.slice();
        } else {
          // At/over the cap, or a no-rows tick: reuse the SAME (now frozen)
          // reference so downstream grid memos don't churn and we never
          // re-clone the array.
          rows = existing.length > 0 ? existing : EMPTY_ROWS;
        }
      }

      const entry: PopulationProgressEntry = {
        uri: event.uri,
        resultSetIndex: rsIndex,
        loadedRowCount: event.loadedRowCount,
        finalRowCount: event.finalRowCount,
        state: event.state,
        columns,
        mode,
        rows,
      };

      // First paint coalescing. The streaming adapters announce a result set
      // with a columns-only event (`rowsBatch: []`, `loadedRowCount: 0`, see
      // mssqlStreamingExecution.openResultSet) so headers can replace the
      // spinner early; for a fast query the first rows event follows within the
      // same frame, and rendering both produced TWO commits — a headers-only
      // grid that is instantly re-rendered with rows. Defer that first event by
      // one frame: a rows event arriving inside the frame merges into a single
      // commit, and if none arrives the headers still paint at the frame
      // boundary (≤16 ms later than before — imperceptible on a slow query,
      // which is the only case where it happens at all).
      //
      // Deferred ONLY when the event carries nothing to paint but headers:
      // a first event that already reports rows (inline `rowsBatch`, or a
      // windowed source with `loadedRowCount > 0`) applies immediately, and
      // terminal events are never deferred.
      const isTerminal = event.state !== 'populating';
      const hasRowsToPaint = (event.rowsBatch?.length ?? 0) > 0 || event.loadedRowCount > 0;

      if (isFirstEventForUri && !isTerminal && !hasRowsToPaint) {
        pendingRef.current.set(rsIndex, entry);
        if (!frameRef.current) {
          frameRef.current = scheduleFrame(() => {
            frameRef.current = null;
            applyEntries();
          });
        }
        return;
      }

      // Any other event (rows, progress tick, or a terminal 'ready'/'failed'/
      // 'cancelled') applies now and flushes whatever was deferred with it.
      cancelFrame();
      applyEntries(entry);
    });

    return () => {
      cancelFrame();
      pendingRef.current.clear();
      unsubscribe();
    };
  }, [tabId, isExecuting]);

  return entries;
}
